import {
  StyledRangePicker,
  StyledSelect
} from '@features/reports/components/riskAssessment/CommonStyledComponents'
import useFormOptions from '@features/reports/hooks/useFormOptions'
import type { Home, Resident } from '@lmc/ts-common'
import { FilterContainer, GroupedResidentsMultiSelect } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { escapeRegExp } from 'common/utils'
import type dayjs from 'dayjs'
import useResidentsList from 'hooks/useResidentsList'
import { NAMESPACE } from 'locale'
import { riskAssessmentReportConstants as translations } from 'locale/riskAssessmentReports'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import type { RangeValue } from 'types/shared'

export type PerAssessmentFilters = {
  rangePicker: RangeValue<dayjs.Dayjs>
  residents: string[]
  riskAssessment: string | null
}
export type FiltersProps = {
  value: PerAssessmentFilters
  onChange: (value: PerAssessmentFilters) => void
}

export const getName = (item: Resident) =>
  `${item.name.first} ${item.name.last}`

export const DashboardFilters = ({
  value,
  onChange
}: FiltersProps): JSX.Element => {
  const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENT_REPORT)

  const handleInputChange = (newValue: Partial<PerAssessmentFilters>) => {
    const updatedValue: PerAssessmentFilters = {
      ...value,
      ...newValue
    }
    onChange(updatedValue)
  }

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const { residents } = useResidentsList({})

  const formOptions = useFormOptions()

  return (
    <FilterContainer title={t(translations.FILTERS)}>
      <>
        <StyledRangePicker
          format="DD MMM YYYY"
          picker="month"
          value={value.rangePicker}
          onChange={(rangePicker) => handleInputChange({ rangePicker })}
        />

        <StyledSelect
          options={formOptions || []}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) =>
            option?.label.match(new RegExp(escapeRegExp(input), 'i'))
          }
          placeholder={
            <Text level={1}>{t(translations.RISK_ASSESSMENT_FILTER)}</Text>
          }
          onChange={(riskAssessment) => {
            handleInputChange({ riskAssessment: riskAssessment as string })
          }}
        />

        <GroupedResidentsMultiSelect
          residents={residents}
          title={preferredPeopleNominative || ''}
          handleInputChange={(selectedOptions) =>
            handleInputChange({
              residents: selectedOptions as string[]
            })
          }
        />
      </>
    </FilterContainer>
  )
}

export default DashboardFilters
